import { loadTokens, saveTokens, clearTokens } from "../auth/tokenStore";
import { refreshTokens } from "../auth/cognito";

const API_BASE = import.meta.env.VITE_API_BASE as string;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

let onSessionExpired: (() => void) | null = null;
export function setOnSessionExpired(cb: () => void): void {
  onSessionExpired = cb;
}

async function doFetch(path: string, init: RequestInit, idToken: string): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
  });
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const tokens = loadTokens();
  if (!tokens) {
    onSessionExpired?.();
    throw new ApiError(401, { error: "not logged in" });
  }

  let resp = await doFetch(path, init, tokens.idToken);

  if (resp.status === 401) {
    try {
      const refreshed = await refreshTokens(tokens.email, tokens.refreshToken);
      saveTokens({ ...refreshed, email: tokens.email });
      resp = await doFetch(path, init, refreshed.idToken);
    } catch {
      clearTokens();
      onSessionExpired?.();
      throw new ApiError(401, { error: "session expired" });
    }
    // Refresh succeeded but the retried request is still unauthorized (e.g. the
    // user was deactivated server-side between refresh and retry): don't leave
    // the app in a "logged-in" state silently erroring on every query.
    if (resp.status === 401) {
      clearTokens();
      onSessionExpired?.();
      throw new ApiError(401, { error: "session expired" });
    }
  }

  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new ApiError(resp.status, body);
  return body as T;
}

const FILENAME = /filename="([^"]+)"/;

/** Fetch a binary/text attachment from an authenticated endpoint.
 *
 *  The export endpoints need the bearer token, so they cannot be plain
 *  <a href> links the way the presigned S3 links can. A failed export must
 *  throw rather than resolve, or the browser would cheerfully "download" a
 *  JSON error body named report.pdf.
 *
 *  Mirrors apiFetch's session-expiry handling exactly, including the case
 *  where refreshTokens() succeeds but the retried request is still 401
 *  (e.g. the user was deactivated server-side between refresh and retry) --
 *  without that second check this would fall through to the generic
 *  !resp.ok branch below and throw a plain ApiError without clearing the
 *  stale session or notifying the app, leaving the user "logged in" but
 *  erroring on every subsequent request. */
export async function apiFetchBlob(path: string): Promise<{ blob: Blob; filename: string }> {
  const tokens = loadTokens();
  if (!tokens) {
    onSessionExpired?.();
    throw new ApiError(401, { error: "not logged in" });
  }

  let resp = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${tokens.idToken}` },
  });

  if (resp.status === 401) {
    try {
      const refreshed = await refreshTokens(tokens.email, tokens.refreshToken);
      saveTokens({ ...refreshed, email: tokens.email });
      resp = await fetch(`${API_BASE}${path}`, {
        headers: { Authorization: `Bearer ${refreshed.idToken}` },
      });
    } catch {
      clearTokens();
      onSessionExpired?.();
      throw new ApiError(401, { error: "session expired" });
    }
    // Same reasoning as apiFetch's identical check: refresh succeeding does
    // not guarantee the retried request succeeds.
    if (resp.status === 401) {
      clearTokens();
      onSessionExpired?.();
      throw new ApiError(401, { error: "session expired" });
    }
  }

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new ApiError(resp.status, body);
  }

  const disposition = resp.headers.get("Content-Disposition") ?? "";
  const match = FILENAME.exec(disposition);
  return { blob: await resp.blob(), filename: match ? match[1] : "download" };
}

/** Trigger a browser download for a blob fetched with apiFetchBlob. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
